import { styled } from "goober";
import * as z from "zod";
import { branchVolumeSeedSchema } from "../../shared/mutators/seedBranchVolumes";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { SystemNotice } from "../components/Chat/SystemMessages";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { Text } from "../components/Text";
import { useToolOutput } from "../hooks/useToolOutput";

type Source = z.infer<typeof branchVolumeSeedSchema>;
type State = "pending" | "interrupted" | "done" | "kept" | "failed" | "settled";

const inputSchema = z.object({
  from: branchVolumeSeedSchema,
  reason: z.string().optional(),
});

const outputSchema = z.object({
  copied: z.array(z.string()),
  keptExisting: z.array(z.string()),
});

const errorSchema = z.object({ error: z.string() });

export function parseSeedBranchStorageInput(
  input: unknown
): { from: Source; reason: string | null } | null {
  const parsed = inputSchema.safeParse(input);
  if (!parsed.success) return null;
  const reason = parsed.data.reason?.trim();
  return { from: parsed.data.from, reason: reason ? reason : null };
}

export function seedBranchStorageState({
  completed,
  isError,
  interrupted,
  output,
}: {
  completed: boolean;
  isError: boolean;
  interrupted: boolean;
  output: unknown;
}): State {
  if (!completed) return interrupted ? "interrupted" : "pending";
  if (isError) return "failed";
  const parsed = outputSchema.safeParse(output);
  if (!parsed.success) return "settled";
  return parsed.data.copied.length === 0 && parsed.data.keptExisting.length > 0
    ? "kept"
    : "done";
}

function seedBranchStorageLabel(from: Source, state: State): string {
  if (from === "empty") {
    switch (state) {
      case "pending":
        return "Starting this draft with empty storage";
      case "interrupted":
        return "Setting up this draft’s storage was interrupted";
      case "failed":
        return "Couldn’t set up this draft’s storage";
      case "done":
      case "kept":
      case "settled":
        return "Started this draft with empty storage";
    }
  }
  const data = from === "live" ? "live’s data" : "the source branch’s data";
  switch (state) {
    case "pending":
      return `Copying ${data} into this draft`;
    case "interrupted":
      return `Copying ${data} into this draft was interrupted`;
    case "failed":
      return `Couldn’t copy ${data} into this draft`;
    case "kept":
      return `This draft already had data of its own, so ${data} wasn’t copied`;
    case "done":
      return `Copied ${data} into this draft`;
    case "settled":
      return `Set up this draft’s storage from ${data}`;
  }
}

function SeedBranchStorageHeader({
  toolUse,
  result,
  interrupted,
}: ToolUseProps) {
  const state = seedBranchStorageState({
    completed: result !== null,
    isError: result?.isError ?? false,
    interrupted,
    output: result?.output,
  });
  const parsed = parseSeedBranchStorageInput(toolUse.input);
  return (
    <ToolStatus
      icon="box"
      text={
        parsed
          ? seedBranchStorageLabel(parsed.from, state)
          : "Setting up this draft’s storage"
      }
      pending={state === "pending"}
    />
  );
}

export const seedBranchStorage: ChatTool = {
  name: "seedBranchStorage",
  Header: SeedBranchStorageHeader,
  alwaysLoadOutput: true,
};

const Notice = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 100%;
`;

const Detail = styled("div")`
  padding: 0 10px 0 24px;
`;

export function SeedBranchStorageNotice({
  entryId,
  input,
  completed,
  isError,
  interrupted,
}: {
  entryId: string;
  input: unknown;
  completed: boolean;
  isError: boolean;
  interrupted: boolean;
}) {
  const { output } = useToolOutput(entryId, completed);
  const parsed = parseSeedBranchStorageInput(input);
  if (!parsed) return null;
  const state = seedBranchStorageState({
    completed,
    isError,
    interrupted,
    output,
  });
  const detail =
    state === "failed"
      ? errorSchema.safeParse(output).data?.error
      : parsed.reason;
  return (
    <Notice role="status">
      <SystemNotice
        icon="box"
        text={seedBranchStorageLabel(parsed.from, state)}
      />
      {detail && (
        <Detail>
          <Text variant="text-small" color="var(--text-tertiary)">
            {detail}
          </Text>
        </Detail>
      )}
    </Notice>
  );
}
